import { useState, useEffect, useCallback } from 'react';
import { BarChart3, TrendingUp, Users, DollarSign, Clock, Download, Target, Layers, ChevronDown, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { theme } from '../../lib/theme';
import LoadingSpinner from '../common/LoadingSpinner';

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'agents', label: 'Agent Performance' },
  { id: 'leads', label: 'Lead Sources' },
  { id: 'response', label: 'Response Time' },
  { id: 'speed_to_lead', label: 'Speed-to-Lead' },
  { id: 'funnel', label: 'Conversion Funnel' },
  { id: 'commission', label: 'Commission' },
  { id: 'production', label: 'Production' },
];

function KpiCard({ label, value, sub, colorValue }) {
  const getColor = () => {
    switch (colorValue) {
      case 'leads':
        return theme.colors.info;
      case 'revenue':
        return theme.colors.success;
      case 'rate':
        return theme.colors.accent;
      case 'primary':
        return theme.colors.primary;
      default:
        return theme.colors.text;
    }
  };

  const color = getColor();

  return (
    <div
      style={{
        backgroundColor: theme.colors.card,
        borderRadius: theme.radius.lg,
        border: `1px solid ${theme.colors.border}`,
        padding: 20,
        boxShadow: theme.shadow.sm,
        transition: 'all 0.2s ease',
      }}
    >
      <p
        style={{
          fontSize: '0.875rem',
          color: theme.colors.textLight,
          marginBottom: 4,
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: '1.875rem',
          fontWeight: 'bold',
          color: color,
          marginTop: 8,
        }}
      >
        {value}
      </p>
      {sub && (
        <p
          style={{
            fontSize: '0.75rem',
            color: theme.colors.textLight,
            marginTop: 4,
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function MiniBar({ items, colorMap }) {
  const max = Math.max(...items.map(i => i.value), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item, idx) => (
        <div key={idx}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.875rem',
              marginBottom: 6,
            }}
          >
            <span style={{ color: theme.colors.text }}>{item.label}</span>
            <span
              style={{
                fontWeight: 500,
                color: theme.colors.text,
              }}
            >
              {item.value}
            </span>
          </div>
          <div
            style={{
              height: 12,
              backgroundColor: theme.colors.bg,
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 999,
                width: `${(item.value / max) * 100}%`,
                backgroundColor: colorMap?.[idx] || theme.colors.primary,
                transition: 'all 0.3s ease',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function FunnelChart({ stages }) {
  const max = stages[0]?.count || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {stages.map((s, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 128,
              fontSize: '0.875rem',
              color: theme.colors.textLight,
              textAlign: 'right',
            }}
          >
            {s.stage}
          </div>
          <div
            style={{
              flex: 1,
              height: 32,
              backgroundColor: theme.colors.bg,
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 4,
                width: `${(s.count / max) * 100}%`,
                backgroundColor: `hsl(${240 - i * 30}, 70%, 60%)`,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: 8,
              }}
            >
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#ffffff',
                }}
              >
                {s.count}
              </span>
            </div>
          </div>
          {s.rate != null && (
            <span
              style={{
                fontSize: '0.75rem',
                color: theme.colors.textLight,
                width: 48,
              }}
            >
              {s.rate}%
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function SimpleTable({ columns, rows, onExport, onExportExcel, expandableContent, expandedRows = new Set() }) {
  const [localExpanded, setLocalExpanded] = useState(new Set());
  const expanded = expandedRows && expandedRows.size > 0 ? expandedRows : localExpanded;

  const toggleRow = (rowIdx) => {
    const newSet = new Set(expanded);
    if (newSet.has(rowIdx)) {
      newSet.delete(rowIdx);
    } else {
      newSet.add(rowIdx);
    }
    setLocalExpanded(newSet);
  };

  const hasExpandable = expandableContent != null;
  const colsWithExpand = hasExpandable ? [{ key: '_expand', label: '', render: null }, ...columns] : columns;

  return (
    <div>
      {(onExport || onExportExcel) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
          {onExportExcel && (
            <button
              onClick={onExportExcel}
              style={{
                fontSize: '0.875rem',
                color: theme.colors.primary,
                fontWeight: 500,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '8px 12px',
                borderRadius: theme.radius.sm,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.bg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FileSpreadsheet size={16} />
              Export Excel
            </button>
          )}
          {onExport && (
            <button
              onClick={onExport}
              style={{
                fontSize: '0.875rem',
                color: theme.colors.primary,
                fontWeight: 500,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '8px 12px',
                borderRadius: theme.radius.sm,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.bg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Download size={16} />
              Export CSV
            </button>
          )}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            fontSize: '0.875rem',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
              {colsWithExpand.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: 'left',
                    padding: col.key === '_expand' ? '12px 6px' : '12px 12px',
                    backgroundColor: theme.colors.bg,
                    color: theme.colors.textLight,
                    fontWeight: 600,
                    borderBottom: `1px solid ${theme.colors.border}`,
                    width: col.key === '_expand' ? 40 : 'auto',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colsWithExpand.length}
                  style={{
                    padding: 32,
                    textAlign: 'center',
                    color: theme.colors.textLight,
                  }}
                >
                  No data
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tbody key={i}>
                  <tr
                    style={{
                      backgroundColor: i % 2 === 0 ? theme.colors.card : theme.colors.bg,
                      borderBottom: `1px solid ${theme.colors.border}`,
                      transition: 'all 0.2s ease',
                      cursor: hasExpandable ? 'pointer' : 'default',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f9f8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        i % 2 === 0 ? theme.colors.card : theme.colors.bg;
                    }}
                  >
                    {hasExpandable && (
                      <td
                        style={{
                          padding: '12px 6px',
                          color: theme.colors.text,
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleRow(i)}
                      >
                        {expanded.has(i) ? (
                          <ChevronDown size={16} color={theme.colors.primary} />
                        ) : (
                          <ChevronRight size={16} color={theme.colors.textLight} />
                        )}
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: '12px 12px',
                          color: theme.colors.text,
                        }}
                        onClick={hasExpandable ? () => toggleRow(i) : undefined}
                      >
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                  {hasExpandable && expanded.has(i) && (
                    <tr
                      style={{
                        backgroundColor: theme.colors.bg,
                        borderBottom: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      <td colSpan={colsWithExpand.length} style={{ padding: 0 }}>
                        <div style={{ padding: '16px 12px', borderLeft: `3px solid ${theme.colors.primary}` }}>
                          {expandableContent(row)}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 20px',
        minHeight: 300,
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: theme.colors.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <BarChart3 size={40} color={theme.colors.primary} />
      </div>
      <h2
        style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: theme.colors.text,
          marginBottom: 8,
        }}
      >
        Not enough data yet
      </h2>
      <p
        style={{
          fontSize: '0.875rem',
          color: theme.colors.textLight,
          textAlign: 'center',
          maxWidth: 400,
          lineHeight: 1.5,
        }}
      >
        Reports will populate as you add deals, leads, and close transactions. Add your first lead to get started!
      </p>
    </div>
  );
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dateRange, setDateRange] = useState('30d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage?.getItem?.('sb-token') || '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const getReportType = (tab) => {
    const mapping = {
      agents: 'agent_performance',
      leads: 'lead_source_roi',
      response: 'response_time',
      speed_to_lead: 'response_time',
      funnel: 'conversion',
    };
    return mapping[tab] || tab;
  };

  const dateParams = useCallback(() => {
    let start, end;
    const now = new Date();
    end = now.toISOString().split('T')[0];

    if (dateRange === 'custom' && customStartDate && customEndDate) {
      start = customStartDate;
      end = customEndDate;
    } else {
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
      start = new Date(now - days * 86400000).toISOString().split('T')[0];
    }
    return `start=${start}&end=${end}`;
  }, [dateRange, customStartDate, customEndDate]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const reportType = getReportType(activeTab);
      const resp = await fetch(`/api/analytics?report=${reportType}&${dateParams()}`, { headers });
      if (resp.ok) {
        const json = await resp.json();
        setData(json);
      }
    } catch (e) {
      /* silent */
    }
    setLoading(false);
  }, [activeTab, dateParams, token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const exportCsv = async () => {
    try {
      const reportType = getReportType(activeTab);
      const resp = await fetch(`/api/analytics?report=${reportType}&${dateParams()}&format=csv`, {
        headers,
      });
      if (resp.ok) {
        const text = await resp.text();
        const blob = new Blob([text], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeTab}-report.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      /* silent */
    }
  };

  const exportExcel = async () => {
    try {
      const reportType = getReportType(activeTab);
      const resp = await fetch(`/api/analytics?report=${reportType}&${dateParams()}&format=xlsx`, {
        headers,
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeTab}-report.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      /* silent */
    }
  };

  const fmt = (n) => (n != null ? n.toLocaleString() : '—');
  const money = (n) => (n != null ? `$${n.toLocaleString()}` : '—');
  const pct = (n) => (n != null ? `${n}%` : '—');

  const renderDashboard = () => {
    if (!data) return <EmptyState />;
    const d = data;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          <KpiCard label="Total Leads" value={fmt(d.total_leads)} colorValue="leads" />
          <KpiCard label="Active Deals" value={fmt(d.active_deals)} colorValue="primary" />
          <KpiCard label="Pipeline Value" value={money(d.pipeline_value)} colorValue="revenue" />
          <KpiCard
            label="Conversion Rate"
            value={pct(d.conversion_rate)}
            colorValue="rate"
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          <KpiCard label="Tasks Due Today" value={fmt(d.tasks_due_today)} colorValue="primary" />
          <KpiCard label="Inbox Items" value={fmt(d.inbox_pending)} colorValue="primary" />
          <KpiCard
            label="Avg Response Time"
            value={d.avg_response_time || '—'}
            sub="minutes"
            colorValue="primary"
          />
          <KpiCard label="Avg Lead Score" value={fmt(d.avg_lead_score)} colorValue="primary" />
        </div>
        {d.lead_score_distribution && (
          <div
            style={{
              backgroundColor: theme.colors.card,
              borderRadius: theme.radius.lg,
              border: `1px solid ${theme.colors.border}`,
              padding: 20,
              boxShadow: theme.shadow.sm,
            }}
          >
            <h3
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: theme.colors.text,
                marginBottom: 16,
              }}
            >
              Lead Score Distribution
            </h3>
            <MiniBar
              items={Object.entries(d.lead_score_distribution).map(([k, v]) => ({
                label: k,
                value: v,
              }))}
              colorMap={[theme.colors.danger, theme.colors.warning, theme.colors.success, theme.colors.info]}
            />
          </div>
        )}
      </div>
    );
  };

  const renderPipeline = () => {
    if (!data?.stages) return <EmptyState />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            border: `1px solid ${theme.colors.border}`,
            padding: 20,
            boxShadow: theme.shadow.sm,
          }}
        >
          <h3
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme.colors.text,
              marginBottom: 16,
            }}
          >
            Deals by Stage
          </h3>
          <MiniBar
            items={data.stages.map((s) => ({
              label: `${s.stage} (${money(s.value)})`,
              value: s.count,
            }))}
            colorMap={['#818cf8', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81']}
          />
        </div>
        <SimpleTable
          columns={[
            { key: 'stage', label: 'Stage' },
            { key: 'count', label: 'Deals', render: (v) => fmt(v) },
            { key: 'value', label: 'Value', render: (v) => money(v) },
            { key: 'avg_days', label: 'Avg Days' },
          ]}
          rows={data.stages}
          onExport={exportCsv}
          onExportExcel={exportExcel}
        />
      </div>
    );
  };

  const renderAgents = () => {
    if (!data?.agents) return <EmptyState />;
    const sorted = [...data.agents].sort((a, b) => (b.closed_volume || 0) - (a.closed_volume || 0));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          {sorted.slice(0, 3).map((a, i) => {
            const medals = ['🥇', '🥈', '🥉'];
            return (
              <div
                key={i}
                style={{
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radius.lg,
                  border:
                    i === 0
                      ? `2px solid ${theme.colors.accent}`
                      : `1px solid ${theme.colors.border}`,
                  padding: 20,
                  boxShadow: theme.shadow.sm,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <span style={{ fontSize: '1.25rem' }}>{medals[i]}</span>
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontWeight: 600,
                        color: theme.colors.text,
                        fontSize: '0.95rem',
                      }}
                    >
                      {a.name}
                    </p>
                    <p
                      style={{
                        fontSize: '0.75rem',
                        color: theme.colors.textLight,
                      }}
                    >
                      #{i + 1} Top Performer
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: theme.colors.textLight }}>Volume</span>
                    <span style={{ color: theme.colors.text, fontWeight: 600 }}>{money(a.closed_volume)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: theme.colors.textLight }}>Deals</span>
                    <span style={{ color: theme.colors.text, fontWeight: 600 }}>{fmt(a.closed_deals)}</span>
                  </div>
                  {a.goal_volume && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: 8 }}>
                        <span style={{ color: theme.colors.textLight }}>Goal Progress</span>
                        <span style={{ color: theme.colors.accent, fontWeight: 600 }}>
                          {Math.round(((a.closed_volume || 0) / (a.goal_volume || 1)) * 100)}%
                        </span>
                      </div>
                      <div style={{ height: 6, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            backgroundColor: theme.colors.accent,
                            width: `${Math.min(100, ((a.closed_volume || 0) / (a.goal_volume || 1)) * 100)}%`,
                            transition: 'all 0.3s ease',
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <SimpleTable
          columns={[
            { key: 'name', label: 'Agent' },
            { key: 'leads', label: 'Leads', render: (v) => fmt(v) },
            { key: 'active_deals', label: 'Active Deals', render: (v) => fmt(v) },
            { key: 'closed_deals', label: 'Closed', render: (v) => fmt(v) },
            { key: 'closed_volume', label: 'Volume', render: (v) => money(v) },
            { key: 'goal_volume', label: 'Goal Volume', render: (v) => v ? money(v) : '—' },
            { key: 'goal_deals', label: 'Goal Deals', render: (v) => v ? fmt(v) : '—' },
            { key: 'tasks_completed', label: 'Tasks Done', render: (v) => fmt(v) },
          ]}
          rows={sorted}
          onExport={exportCsv}
          onExportExcel={exportExcel}
          expandableContent={(agent) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>Agent Details</h4>
              {agent.goal_volume && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                    <span style={{ color: theme.colors.textLight }}>Revenue Goal Progress</span>
                    <span style={{ color: theme.colors.text, fontWeight: 600 }}>
                      {money(agent.closed_volume || 0)} / {money(agent.goal_volume)}
                    </span>
                  </div>
                  <div style={{ height: 8, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        backgroundColor: theme.colors.success,
                        width: `${Math.min(100, ((agent.closed_volume || 0) / (agent.goal_volume || 1)) * 100)}%`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              )}
              {agent.goal_deals && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                    <span style={{ color: theme.colors.textLight }}>Deal Goal Progress</span>
                    <span style={{ color: theme.colors.text, fontWeight: 600 }}>
                      {fmt(agent.closed_deals || 0)} / {fmt(agent.goal_deals)}
                    </span>
                  </div>
                  <div style={{ height: 8, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        backgroundColor: theme.colors.info,
                        width: `${Math.min(100, ((agent.closed_deals || 0) / (agent.goal_deals || 1)) * 100)}%`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div style={{ padding: 8, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Active Deals</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: 600, color: theme.colors.primary, margin: 0, marginTop: 4 }}>{fmt(agent.active_deals)}</p>
                </div>
                <div style={{ padding: 8, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Tasks Done</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: 600, color: theme.colors.primary, margin: 0, marginTop: 4 }}>{fmt(agent.tasks_completed)}</p>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderLeadSources = () => {
    if (!data?.sources) return <EmptyState />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            border: `1px solid ${theme.colors.border}`,
            padding: 20,
            boxShadow: theme.shadow.sm,
          }}
        >
          <h3
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme.colors.text,
              marginBottom: 16,
            }}
          >
            Leads by Source
          </h3>
          <MiniBar
            items={data.sources.map((s) => ({
              label: s.source || 'Unknown',
              value: s.count,
            }))}
            colorMap={['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e']}
          />
        </div>
        <SimpleTable
          columns={[
            { key: 'source', label: 'Source', render: (v) => v || 'Unknown' },
            { key: 'count', label: 'Leads', render: (v) => fmt(v) },
            { key: 'converted', label: 'Converted', render: (v) => fmt(v) },
            { key: 'conversion_rate', label: 'Rate', render: (v) => pct(v) },
            { key: 'cost_per_lead', label: 'Cost/Lead', render: (v) => v ? money(v) : '—' },
            { key: 'revenue_per_lead', label: 'Revenue/Lead', render: (v) => v ? money(v) : '—' },
            { key: 'estimated_roi', label: 'Est. ROI', render: (v) => v ? pct(v) : '—' },
            { key: 'avg_score', label: 'Avg Score', render: (v) => fmt(v) },
          ]}
          rows={data.sources}
          onExport={exportCsv}
          onExportExcel={exportExcel}
          expandableContent={(source) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>Source Performance</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Total Leads</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.info, margin: 0, marginTop: 4 }}>{fmt(source.count)}</p>
                </div>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Conversion Rate</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.success, margin: 0, marginTop: 4 }}>{pct(source.conversion_rate)}</p>
                </div>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Est. ROI</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.accent, margin: 0, marginTop: 4 }}>{source.estimated_roi ? pct(source.estimated_roi) : '—'}</p>
                </div>
              </div>
              {source.cost_per_lead || source.revenue_per_lead ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: theme.colors.textLight }}>Avg Cost per Lead</span>
                    <span style={{ color: theme.colors.text, fontWeight: 600 }}>{source.cost_per_lead ? money(source.cost_per_lead) : 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: theme.colors.textLight }}>Revenue per Lead</span>
                    <span style={{ color: theme.colors.text, fontWeight: 600 }}>{source.revenue_per_lead ? money(source.revenue_per_lead) : 'N/A'}</span>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        />
      </div>
    );
  };

  const renderResponseTime = () => {
    if (!data) return <EmptyState />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          <KpiCard label="Avg Response" value={`${data.avg_minutes || '—'}m`} colorValue="primary" />
          <KpiCard label="Median Response" value={`${data.median_minutes || '—'}m`} colorValue="primary" />
          <KpiCard label="Under 5 Min" value={pct(data.under_5_min_pct)} colorValue="success" />
          <KpiCard label="Speed to Lead" value={data.speed_to_lead || '—'} colorValue="accent" />
        </div>
        {data.by_channel && (
          <SimpleTable
            columns={[
              { key: 'channel', label: 'Channel' },
              { key: 'avg_minutes', label: 'Avg (min)' },
              { key: 'count', label: 'Responses', render: (v) => fmt(v) },
            ]}
            rows={data.by_channel}
            onExport={exportCsv}
            onExportExcel={exportExcel}
          />
        )}
      </div>
    );
  };

  const renderSpeedToLead = () => {
    if (!data?.by_agent && !data?.agents) return <EmptyState />;
    const agents = data.by_agent || data.agents || [];
    const sorted = [...agents].sort((a, b) => (a.avg_response_minutes || 9999) - (b.avg_response_minutes || 9999));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          <KpiCard
            label="Team Avg Response"
            value={data.avg_minutes ? `${data.avg_minutes}m` : '—'}
            colorValue="primary"
          />
          <KpiCard
            label="Fastest Responder"
            value={sorted[0]?.avg_response_minutes ? `${sorted[0].avg_response_minutes}m` : '—'}
            sub={sorted[0]?.name || ''}
            colorValue="success"
          />
          <KpiCard
            label="Contacted in 5 min"
            value={pct(data.under_5_min_pct)}
            colorValue="success"
          />
          <KpiCard
            label="Contacted in 30 min"
            value={pct(data.under_30_min_pct)}
            colorValue="info"
          />
        </div>

        <div
          style={{
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            border: `1px solid ${theme.colors.border}`,
            padding: 20,
            boxShadow: theme.shadow.sm,
          }}
        >
          <h3
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme.colors.text,
              marginBottom: 16,
            }}
          >
            Agent Response Leaderboard
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sorted.map((agent, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  backgroundColor: idx === 0 ? 'rgba(232, 145, 58, 0.08)' : theme.colors.bg,
                  borderRadius: theme.radius.md,
                  border: idx === 0 ? `1px solid ${theme.colors.accent}` : `1px solid ${theme.colors.border}`,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: idx === 0 ? theme.colors.accent : theme.colors.primary,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {idx + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 500, color: theme.colors.text }}>{agent.name}</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: theme.colors.textLight }}>
                    {fmt(agent.response_count || 0)} responses
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: '1.125rem',
                      color: theme.colors.primary,
                    }}
                  >
                    {agent.avg_response_minutes ? `${agent.avg_response_minutes.toFixed(1)}m` : '—'}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: theme.colors.textLight }}>avg time</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <SimpleTable
          columns={[
            { key: 'name', label: 'Agent' },
            { key: 'response_count', label: 'Responses', render: (v) => fmt(v || 0) },
            { key: 'avg_response_minutes', label: 'Avg Time (min)', render: (v) => v ? v.toFixed(1) : '—' },
            { key: 'under_5_min_pct', label: '< 5 min %', render: (v) => pct(v) },
            { key: 'under_15_min_pct', label: '< 15 min %', render: (v) => pct(v) },
            { key: 'under_30_min_pct', label: '< 30 min %', render: (v) => pct(v) },
            { key: 'under_1hr_pct', label: '< 1 hr %', render: (v) => pct(v) },
          ]}
          rows={sorted}
          onExport={exportCsv}
          onExportExcel={exportExcel}
          expandableContent={(agent) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>Response Performance</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                    <span style={{ color: theme.colors.textLight }}>Within 5 minutes</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{pct(agent.under_5_min_pct)}</span>
                  </div>
                  <div style={{ height: 8, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        backgroundColor: theme.colors.success,
                        width: `${Math.min(100, agent.under_5_min_pct || 0)}%`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                    <span style={{ color: theme.colors.textLight }}>Within 15 minutes</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{pct(agent.under_15_min_pct)}</span>
                  </div>
                  <div style={{ height: 8, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        backgroundColor: theme.colors.info,
                        width: `${Math.min(100, agent.under_15_min_pct || 0)}%`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                    <span style={{ color: theme.colors.textLight }}>Within 30 minutes</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{pct(agent.under_30_min_pct)}</span>
                  </div>
                  <div style={{ height: 8, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        backgroundColor: theme.colors.warning,
                        width: `${Math.min(100, agent.under_30_min_pct || 0)}%`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                    <span style={{ color: theme.colors.textLight }}>Within 1 hour</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{pct(agent.under_1hr_pct)}</span>
                  </div>
                  <div style={{ height: 8, backgroundColor: theme.colors.bg, borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        backgroundColor: theme.colors.primary,
                        width: `${Math.min(100, agent.under_1hr_pct || 0)}%`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderFunnel = () => {
    if (!data?.stages) return <EmptyState />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            border: `1px solid ${theme.colors.border}`,
            padding: 20,
            boxShadow: theme.shadow.sm,
          }}
        >
          <h3
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme.colors.text,
              marginBottom: 16,
            }}
          >
            Conversion Funnel
          </h3>
          <FunnelChart stages={data.stages} />
        </div>
        <SimpleTable
          columns={[
            { key: 'stage', label: 'Stage' },
            { key: 'count', label: 'Count', render: (v) => fmt(v) },
            { key: 'rate', label: 'Conversion', render: (v) => pct(v) },
            { key: 'drop_off', label: 'Drop-off', render: (v) => fmt(v) },
          ]}
          rows={data.stages}
          onExport={exportCsv}
          onExportExcel={exportExcel}
          expandableContent={(stage) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>Stage Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Deals in Stage</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.primary, margin: 0, marginTop: 4 }}>{fmt(stage.count)}</p>
                </div>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Conversion Rate</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.success, margin: 0, marginTop: 4 }}>{pct(stage.rate)}</p>
                </div>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Drop-off</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.danger, margin: 0, marginTop: 4 }}>{fmt(stage.drop_off)}</p>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderCommission = () => {
    if (!data?.agents) return <EmptyState />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          <KpiCard
            label="Total Commission"
            value={money(data.total_commission)}
            colorValue="revenue"
          />
          <KpiCard label="Total Volume" value={money(data.total_volume)} colorValue="primary" />
          <KpiCard label="Avg per Deal" value={money(data.avg_per_deal)} colorValue="primary" />
        </div>
        <SimpleTable
          columns={[
            { key: 'name', label: 'Agent' },
            { key: 'closed_volume', label: 'Volume', render: (v) => money(v) },
            { key: 'commission', label: 'Commission', render: (v) => money(v) },
            { key: 'deals', label: 'Deals', render: (v) => fmt(v) },
          ]}
          rows={data.agents}
          onExport={exportCsv}
          onExportExcel={exportExcel}
          expandableContent={(agent) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>Commission Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ color: theme.colors.textLight }}>Total Volume</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{money(agent.closed_volume)}</span>
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ color: theme.colors.textLight }}>Total Commission</span>
                    <span style={{ fontWeight: 600, color: theme.colors.success }}>{money(agent.commission)}</span>
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ color: theme.colors.textLight }}>Deals Closed</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{fmt(agent.deals)}</span>
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ color: theme.colors.textLight }}>Avg per Deal</span>
                    <span style={{ fontWeight: 600, color: theme.colors.text }}>{money((agent.closed_volume || 0) / (agent.deals || 1))}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderProduction = () => {
    if (!data?.months) return <EmptyState />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <SimpleTable
          columns={[
            { key: 'month', label: 'Month' },
            { key: 'deals_closed', label: 'Closed', render: (v) => fmt(v) },
            { key: 'volume', label: 'Volume', render: (v) => money(v) },
            { key: 'new_leads', label: 'New Leads', render: (v) => fmt(v) },
            { key: 'avg_days_to_close', label: 'Avg Days' },
          ]}
          rows={data.months}
          onExport={exportCsv}
          onExportExcel={exportExcel}
          expandableContent={(month) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>Monthly Performance</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Deals Closed</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.info, margin: 0, marginTop: 4 }}>{fmt(month.deals_closed)}</p>
                </div>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Total Volume</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.success, margin: 0, marginTop: 4 }}>{money(month.volume)}</p>
                </div>
                <div style={{ padding: 12, backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
                  <p style={{ fontSize: '0.75rem', color: theme.colors.textLight, margin: 0 }}>Avg Days to Close</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.primary, margin: 0, marginTop: 4 }}>{month.avg_days_to_close}</p>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span style={{ color: theme.colors.textLight }}>New Leads Added</span>
                <span style={{ fontWeight: 600, color: theme.colors.text }}>{fmt(month.new_leads)}</span>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderTab = {
    dashboard: renderDashboard,
    pipeline: renderPipeline,
    agents: renderAgents,
    leads: renderLeadSources,
    response: renderResponseTime,
    speed_to_lead: renderSpeedToLead,
    funnel: renderFunnel,
    commission: renderCommission,
    production: renderProduction,
  };

  return (
    <div
      style={{
        maxWidth: '80rem',
        margin: '0 auto',
        padding: '24px 16px',
        backgroundColor: theme.colors.bg,
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <h1
            style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: theme.colors.text,
            }}
          >
            Reports & Analytics
          </h1>
        </div>

        {/* Date Range Picker */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {['7d', '30d', '90d', '1y'].map((range) => (
              <button
                key={range}
                onClick={() => {
                  setDateRange(range);
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  borderRadius: theme.radius.sm,
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor:
                    dateRange === range ? theme.colors.primary : theme.colors.card,
                  color: dateRange === range ? '#ffffff' : theme.colors.text,
                  boxShadow:
                    dateRange === range
                      ? `0 0 0 1px ${theme.colors.primary}`
                      : `0 0 0 1px ${theme.colors.border}`,
                }}
              >
                {range === '1y' ? '1 Year' : range}
              </button>
            ))}
            <button
              onClick={() => setDateRange('custom')}
              style={{
                padding: '8px 12px',
                fontSize: '0.875rem',
                borderRadius: theme.radius.sm,
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor:
                  dateRange === 'custom' ? theme.colors.primary : theme.colors.card,
                color: dateRange === 'custom' ? '#ffffff' : theme.colors.text,
                boxShadow:
                  dateRange === 'custom'
                    ? `0 0 0 1px ${theme.colors.primary}`
                    : `0 0 0 1px ${theme.colors.border}`,
              }}
            >
              Custom
            </button>
          </div>

          {dateRange === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 500, color: theme.colors.textLight }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    borderRadius: theme.radius.sm,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.card,
                    color: theme.colors.text,
                    fontFamily: 'inherit',
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 500, color: theme.colors.textLight }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    borderRadius: theme.radius.sm,
                    border: `1px solid ${theme.colors.border}`,
                    backgroundColor: theme.colors.card,
                    color: theme.colors.text,
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          overflowX: 'auto',
          paddingBottom: 8,
          marginBottom: 24,
          borderBottom: `1px solid ${theme.colors.border}`,
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 16px',
              fontSize: '0.875rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              borderRadius: `${theme.radius.sm}px ${theme.radius.sm}px 0 0`,
              border: 'none',
              cursor: 'pointer',
              backgroundColor:
                activeTab === tab.id ? theme.colors.card : 'transparent',
              color: activeTab === tab.id ? theme.colors.primary : theme.colors.textLight,
              borderBottom:
                activeTab === tab.id
                  ? `2px solid ${theme.colors.primary}`
                  : 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.backgroundColor = theme.colors.bg;
                e.currentTarget.style.color = theme.colors.text;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = theme.colors.textLight;
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 80,
            paddingBottom: 80,
          }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        renderTab[activeTab]?.()
      )}
    </div>
  );
}
